/**
 * The body of one leader's profile page (`/about-us/leadership-team/<slug>`): breadcrumb, name, job
 * title, a video or photo, the two rich-text biography columns, and the "Experience and Area of
 * Expertise" credentials.
 *
 * Position: rendered only by `components/Leadership/LeadershipProfileContainer.tsx`, which adds
 * the page's SEO head. The data is one language's entry of
 * `src/contentful/leader/getContentfulLeader.ts`, shaped by
 * `src/contentful/leader/helpers/index.ts` → `processContentfulLeaderResponses`: the rich-text
 * columns arrive as JSON strings in `raw` for `components/Shared/model/Contentful.tsx` →
 * `renderRichText`, and `rightColumn`, `videoId` and `credentials` are `null` when the entry
 * leaves them empty.
 *
 * What the Contentful fields switch:
 * - `videoId` set: a `@ryerson/frontend.media` → `Video` with `videoThumbnail` as its poster.
 *   `videoThumbnail` must then resolve to a file; without one, the `videoThumbnail.file.url`
 *   read throws.
 * - `videoId` empty: a still image, `videoThumbnail` when it has a file, otherwise `picture`.
 * - `rightColumn` empty, desktop: `credentials` sits under the left column. `rightColumn` set,
 *   desktop: `credentials` sits under the right column. Mobile always stacks left column, right
 *   column, then `credentials`.
 * - `credentials` empty: no "Experience and Area of Expertise" heading at all.
 *
 * The heading text is `./Content.tsx` → `LeadershipProfileSetupLanguage`.
 */
import React from "react";
import { useApplication } from "@ryerson/frontend.application";
import { ContentSection } from "@ryerson/frontend.layout";
import { Video } from "@ryerson/frontend.media";
import { Breadcrumb } from "@ryerson/frontend.navigation";
import { LeadershipProfileSetupLanguage } from "./Content";
import { Typography } from "@ryerson/frontend.typography";
import { Media } from "@ryerson/frontend.next";
import { useTheme } from "@ryerson/frontend.theme";
import styled from "@emotion/styled";
import { renderRichText } from "@components/Shared/model/Contentful";
import { css } from "@emotion/react";
import { VideoWrapperProps } from "@components/Helper/common";
import { getVideoPadding } from "@components/Helper/commonStyles";
import { ProcessedContentfulLeaderFieldsForLeaderPage } from "@contentful/leader/types";
import Spacer from "@components/Shared/Spacer/Spacer";

/**
 * The Gatsby-era GraphQL shape of a leader (`node_locale` is a Gatsby source field). Nothing
 * imports it since the Next.js migration; the live shape is
 * `src/contentful/leader/types/index.ts` → `ProcessedContentfulLeaderFieldsForLeaderPage`.
 */
export type LeaderContentfulType = {
	credentials: any;
	leftColumn: any;
	rightColumn?: any;
	fullName: string;
	node_locale: "en-US" | "fr-CA" | "es-MX";
	picture: any;
	videoThumbnail: any;
	videoId: string;
	slug: string;
	title: string;
};

/** The profile page's fixed copy, per language in `./Content.tsx`. */
export type LeadershipProfileSetup = {
	/** Heading above the leader's `credentials`. */
	experienceLabel: string;
};

const BreadcrumbContainer = styled.div`
	margin-top: 32px;
	${(props: any) => {
		const { theme } = props;
		return css`
			@media only screen and (max-width: ${theme.breakpoints.lg}) {
				margin-top: 8px;
			}
		`;
	}}
`;

const Divider = styled.hr`
	margin-top: 10px;
	margin-bottom: 0px;
	opacity: 0.1;
`;

const Left = styled.div`
	display: inline-block;
	vertical-align: top;
	width: calc(100% - 660px);
`;

const Right = styled.div`
	display: inline-block;
	width: 560px;
	margin-left: 100px;
	height: 420px;
`;

const ColContainer = styled.div`
	display: inline-block;
	width: calc(50% - 20px);
	vertical-align: top;
`;


const VideoWrapper = styled.div`
	${(props: VideoWrapperProps) => {
		const { theme, buttonOrientation = "left", buttonSize = "md" } = props;
		return css`
			@media only screen and (max-width: ${theme.breakpoints.lg}) {
				height: 100%;
				${buttonOrientation === "center"
					? 0
					: getVideoPadding(buttonOrientation, true, buttonSize)};
			}
			@media only screen and (min-width: ${theme.breakpoints.lg}) {
				width: 100%;
				height: 100%;
				${buttonOrientation === "center"
					? 0
					: getVideoPadding(buttonOrientation, false, buttonSize)};
			}
		`;
	}}
`;

interface LeadershipProfileProps {
	/** The current language's entry of the leader's profile. */
	contentfulLeaderForLanguage: ProcessedContentfulLeaderFieldsForLeaderPage;
}

/**
 * Renders one leader's profile, desktop and mobile layouts, from already-localised Contentful data.
 * Reads the language only for the credentials heading.
 */
const LeadershipProfile: React.FC<LeadershipProfileProps> = ({ contentfulLeaderForLanguage }) => {
	const { theme } = useTheme();
	const {
		localization: { language },
	} = useApplication();

	return (
		<>
			<Media greaterThanOrEqual="lg">
				<ContentSection
					type="split"
					split={{
						splitDirection: "vertical",
						splitColors: [
							{
								color: theme.colors.tertiary.background,
								percentage: "60%",
							},
							{
								color: theme.colors.primary.background,
								percentage: "40%",
							},
						],
					}}
				>
					<BreadcrumbContainer theme={theme}>
						<Breadcrumb type="tertiary" size="xs" />
					</BreadcrumbContainer>
					<Left>
						<Typography
							variant="h1"
							css={css`
								font-size: 75px;
								margin-top: 21px;
								margin-bottom: 32px;
							`}
							type="tertiary"
						>
							{contentfulLeaderForLanguage.fullName}
						</Typography>
						<Typography
							variant="div"
							type="tertiary"
							css={css`
								position: relative;
								display: block;
								width: 100%;
							`}
						>
							{contentfulLeaderForLanguage.title}
						</Typography>
						<Divider
							css={css`
								color: ${theme.colors.primary.white};
							`}
						/>
					</Left>
					<Right>
						{contentfulLeaderForLanguage.videoId &&
						contentfulLeaderForLanguage.videoId.length > 0 ? (
							<VideoWrapper theme={theme} buttonOrientation="bottom">
								<Video
									videoId={contentfulLeaderForLanguage.videoId}
									imageUrl={contentfulLeaderForLanguage.videoThumbnail.file.url}
									buttonOrientation="bottom"
								></Video>
							</VideoWrapper>
						) : (
							<img
								src={
									contentfulLeaderForLanguage.videoThumbnail &&
									contentfulLeaderForLanguage.videoThumbnail.file
										? contentfulLeaderForLanguage.videoThumbnail.file.url
										: contentfulLeaderForLanguage.picture.file.url
								}
								alt={contentfulLeaderForLanguage.title}
								css={css`
									display: block;
									object-fit: cover;
									max-height: 100%;
									max-width: 100%;
								`}
							/>
						)}
					</Right>
				</ContentSection>
				<ContentSection type="primary" vPadding="0px">
					<ColContainer
						css={css`
							margin-right: 40px;
							margin-top: -140px;
						`}
					>
						{renderRichText(contentfulLeaderForLanguage.leftColumn.raw, "primary")}
						{/*
							Credentials go under the left column only when there is no right column.
						*/}
						{(!contentfulLeaderForLanguage.rightColumn ||
							contentfulLeaderForLanguage.rightColumn === null) && (
							<>
								{contentfulLeaderForLanguage.credentials &&
									contentfulLeaderForLanguage.credentials.length > 0 && (
										<>
											<Typography
												type="primary"
												variant="h4"
												css={css`
													margin-bottom: 21px;
												`}
											>
												{
													LeadershipProfileSetupLanguage[language]
														.experienceLabel
												}
											</Typography>
											<Typography type="primary" variant="p">
												{contentfulLeaderForLanguage.credentials}
											</Typography>
										</>
									)}
							</>
						)}
					</ColContainer>
					<ColContainer>
						{contentfulLeaderForLanguage.rightColumn &&
							contentfulLeaderForLanguage.rightColumn !== null &&
							contentfulLeaderForLanguage.rightColumn.raw && (
								<>
									{renderRichText(
										contentfulLeaderForLanguage.rightColumn.raw,
										"primary"
									)}
									<>
										{contentfulLeaderForLanguage.credentials &&
											contentfulLeaderForLanguage.credentials.length > 0 && (
												<>
													<Typography
														type="primary"
														variant="h4"
														css={css`
															margin-bottom: 21px;
														`}
													>
														{
															LeadershipProfileSetupLanguage[language]
																.experienceLabel
														}
													</Typography>
													<Typography type="primary" variant="p">
														{contentfulLeaderForLanguage.credentials}
													</Typography>
												</>
											)}
									</>
								</>
							)}
					</ColContainer>
					<Spacer css={css`height: 80px;`} />
				</ContentSection>
			</Media>
			<Media lessThan="lg">
				<ContentSection type="tertiary" vPadding="30px">
					<BreadcrumbContainer theme={theme}>
						<Breadcrumb type="tertiary" size="xs" />
					</BreadcrumbContainer>
					<Typography
						variant="h1"
						type="tertiary"
						css={css`
							margin-top: 11px;
							margin-bottom: 24px;
						`}
					>
						{contentfulLeaderForLanguage.fullName}
					</Typography>
					<Typography
						variant="div"
						type="tertiary"
						weight="bold"
						size="lg"
						css={css`
							position: relative;
							display: block;
							margin-bottom: 5px;
							width: 100%;
							padding-right: 70px;
							box-sizing: border-box;
						`}
					>
						{contentfulLeaderForLanguage.title}
					</Typography>
					{contentfulLeaderForLanguage.videoId &&
					contentfulLeaderForLanguage.videoId.length > 0 ? (
						<VideoWrapper theme={theme} buttonOrientation="top-right">
							<Video
								videoId={contentfulLeaderForLanguage.videoId}
								imageUrl={contentfulLeaderForLanguage.videoThumbnail.file.url}
								buttonOrientation="top-right"
							></Video>
						</VideoWrapper>
					) : (
						<img
							src={
								contentfulLeaderForLanguage.videoThumbnail &&
								contentfulLeaderForLanguage.videoThumbnail.file
									? contentfulLeaderForLanguage.videoThumbnail.file.url
									: contentfulLeaderForLanguage.picture.file.url
							}
							alt={contentfulLeaderForLanguage.title}
							css={css`
								display: block;
								max-height: 100%;
								max-width: 100%;
							`}
						/>
					)}
				</ContentSection>
				<ContentSection type="primary" vPadding="30px">
					<Typography size="md">
						{renderRichText(contentfulLeaderForLanguage.leftColumn.raw, "primary")}
					</Typography>
					{contentfulLeaderForLanguage.rightColumn &&
						contentfulLeaderForLanguage.rightColumn !== null &&
						contentfulLeaderForLanguage.rightColumn.raw && (
							<Typography size="md">
								{renderRichText(
									contentfulLeaderForLanguage.rightColumn.raw,
									"primary"
								)}
							</Typography>
						)}
					{contentfulLeaderForLanguage.credentials &&
						contentfulLeaderForLanguage.credentials.length > 0 && (
							<>
								<Typography
									type="primary"
									variant="h3"
									css={css`
										margin-bottom: 0px;
									`}
								>
									{LeadershipProfileSetupLanguage[language].experienceLabel}
								</Typography>
								<Typography type="primary" size="md" variant="p">
									{contentfulLeaderForLanguage.credentials}
								</Typography>
							</>
						)}
				</ContentSection>
			</Media>
		</>
	);
};
export default LeadershipProfile;
